import moment from 'moment';

/**
 * Converts between the date/time an admin picks and the instant the API stores.
 *
 * The API takes and returns ISO 8601 instants in UTC ("2026-12-12T14:28:00.000Z").
 * Form inputs work in the browser's local time with no zone:
 *   <input type="datetime-local">  "2026-12-12T19:58"
 *   <input type="date">            "2026-12-12"
 *   <input type="time">            "19:58"
 * Sending an input's value as-is loses the zone, so always go through these.
 */

const INPUT_FORMATS = {
    'datetime-local': 'YYYY-MM-DDTHH:mm',
    date: 'YYYY-MM-DD',
    time: 'HH:mm',
};

// 24-hour and 12-hour clocks: "9:05", "19:58:30", "7:58 PM", "12:00 AM" (= 00:00), "11:59PM".
const TIME_FORMATS = ['H:mm', 'H:mm:ss', 'H:mm:ss.SSS', 'h:mm A', 'h:mmA', 'h:mm:ss A'];

const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';

/**
 * A Date, moment, epoch ms or ISO 8601 string → a new moment in local time, or null when
 * blank or not a date. A string with no zone ("2026-12-12T19:58", "2026-12-12") is read as
 * local time; one with a zone ("…Z", "…+05:30") keeps its instant.
 */
export const parseDateTime = (value) => {
    if (isBlank(value)) return null;
    const parsed = moment.isMoment(value) || value instanceof Date || typeof value === 'number'
        ? moment(value)
        : moment(String(value).trim(), moment.ISO_8601, true);
    return parsed.isValid() ? parsed : null;
};

/** "19:58", "7:58 pm", "12:00 AM" … → a moment holding that clock time, or null. */
const parseTime = (time) => {
    const parsed = moment(String(time).trim().toUpperCase(), TIME_FORMATS, true);
    return parsed.isValid() ? parsed : null;
};

/**
 * What the admin picked (local time) → ISO 8601 in UTC for the API, or '' when it is blank
 * or not a valid date/time.
 *
 *   toIsoDateTime('2026-12-12T19:58')                         a datetime-local value
 *   toIsoDateTime('2026-12-12', { time: '19:58' })            separate date + time inputs
 *   toIsoDateTime('2026-12-12', { time: '11:59 PM' })         a 12-hour clock
 *   toIsoDateTime('2026-12-12')                               date only → 00:00 local
 *   toIsoDateTime('2026-12-12', { endOf: 'day' })             date only → 23:59:59.999 local
 *   toIsoDateTime('2026-12-12T23:59', { endOf: 'minute' })    → 23:59:59.999, the whole minute
 *
 * `time` replaces the clock part of `value`. `endOf` (any moment unit) is applied last and
 * rounds up to the last millisecond of that unit, for inclusive "until" boundaries.
 */
export const toIsoDateTime = (value, { time, endOf } = {}) => {
    const parsed = parseDateTime(value);
    if (!parsed) return '';
    if (!isBlank(time)) {
        const clock = parseTime(time);
        if (!clock) return '';
        parsed.set({
            hour: clock.hour(),
            minute: clock.minute(),
            second: clock.second(),
            millisecond: clock.millisecond(),
        });
    }
    if (endOf) parsed.endOf(endOf);
    return parsed.toISOString();
};

/**
 * An instant from the API → the local-time value an <input> of `type` expects
 * ('datetime-local' | 'date' | 'time'), or '' when it is blank or not a date.
 *
 *   toInputValue('2026-12-12T14:28:00.000Z')           → '2026-12-12T19:58' in UTC+5:30
 *   toInputValue('2026-12-12T14:28:00.000Z', 'date')   → '2026-12-12'
 *   toInputValue('2026-12-12T14:28:00.000Z', 'time')   → '19:58'
 */
export const toInputValue = (value, type = 'datetime-local') => {
    const parsed = parseDateTime(value);
    return parsed ? parsed.format(INPUT_FORMATS[type] || INPUT_FORMATS['datetime-local']) : '';
};
